import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { DashboardClaim, Guid } from '@lsq-frontend/remote-services/enterprise/common';
import {
    ClassNames,
    Color,
    DateFormat,
    forLifeOf,
    getDynamicRouteLink,
    getDynamicRouterLink,
    getRouterLinkById,
    Interaction,
    LifecycleAware,
    Modal,
    StatusColor,
    Styling
} from '@lsq-frontend/common-ui';
import {
    ClaimService,
    FeatureFlagService,
    getRouterLinkUrlById,
    Icons,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { CheckableRowsService } from '@lsq-frontend/common-ui';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { SupplierDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/supplier-dto';
import {
    ListResponseSupplierDto,
    ListSuppliersRequest
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-suppliers-list/list-suppliers';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ADMIN_BUYERS,
    ADMIN_CREATE_SUPPLIER,
    ADMIN_MANAGE_BUYER_COMPANY,
    ADMIN_MANAGE_SUPPLIER_IMPLEMENTATION,
    ADMIN_MANAGE_SUPPLIER_INFORMATION,
    ADMIN_MANAGE_SUPPLIER_PAYMENT_METHODS,
    atlas
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { EnrollmentStatus, PaymentMethodStatusDisplay } from '../models';
import { AdminSuppliersService } from './admin-suppliers.service';
import { AdminSuppliersBulkActionsService } from './bulk-actions/bulk-actions.service';
import { ToggleSupplierActivationModalComponent } from './modals/toggle-supplier-activation-modal.component';
import { ToggleSupplierEligibilityModalComponent } from './modals/toggle-supplier-eligibility-modal.component';
import { ToggleScfEnabledModalComponent } from './modals/toggle-scf-enabled-modal.component';

@Component({
    selector: 'suppliers-admin',
    styleUrls: ['./admin-suppliers.component.scss'],
    template: `
        <admin-suppliers-bulk-actions
            *ngIf="bulkActionType$ | async"
        ></admin-suppliers-bulk-actions>
        <div class="admin-suppliers" *ngIf="!(bulkActionType$ | async)">
            <page-header
                titleText="Suppliers"
                [backButtonText]="
                    visitedFromBuyerKey && visitedFromBuyerName
                        ? 'Back to buyer'
                        : visitedFromBuyerName
                        ? 'Back to buyers'
                        : null
                "
                (handleBackButtonClick)="navigateBackToBuyer()"
            >
                <lsq-button
                    *ngIf="canEditSuppliers"
                    [style.marginTop]="'1rem'"
                    (buttonClick)="navigateToCreateSuppliersPage()"
                >
                    Add Suppliers
                </lsq-button>
            </page-header>
            <div class="container-fluid page-container">
                <card>
                    <div left-header-content>
                        <lsq-table-checkbox-controls
                            [checkableRowsService]="checkableRowsService"
                            [isFilteredSelectAll]="true"
                            hasActions="true"
                        >
                            <div [style.width]="'24rem'">
                                <lsq-button
                                    appearance="secondary"
                                    size="block"
                                    [style.marginBottom]="'1rem'"
                                    (buttonClick)="bulkSetEligibility(true)"
                                >
                                    Enable Eligibility
                                </lsq-button>
                                <lsq-button
                                    appearance="secondary"
                                    size="block"
                                    [style.marginBottom]="'1rem'"
                                    (buttonClick)="bulkSetEligibility(false)"
                                >
                                    Disable Eligibility
                                </lsq-button>
                                <lsq-button
                                    appearance="secondary"
                                    size="block"
                                    [style.marginBottom]="'1rem'"
                                    (buttonClick)="bulkEnableScf(true)"
                                >
                                    Enable SCF
                                </lsq-button>
                                <lsq-button
                                    appearance="secondary"
                                    size="block"
                                    [style.marginBottom]="'1rem'"
                                    (buttonClick)="bulkEnableScf(false)"
                                >
                                    Disable SCF
                                </lsq-button>
                                <lsq-button
                                    appearance="secondary"
                                    size="block"
                                    [style.marginBottom]="'1rem'"
                                    (buttonClick)="bulkSendEnrollmentEmail()"
                                >
                                    Send Enrollment Email
                                </lsq-button>
                                <lsq-button
                                    appearance="secondary"
                                    size="block"
                                    (buttonClick)="bulkSetFeeStructure()"
                                >
                                    Set Fee Structure
                                </lsq-button>
                            </div>
                        </lsq-table-checkbox-controls>
                    </div>
                    <div
                        right-header-content
                        class="suppliers-filters"
                        [formGroup]="supplierFiltersFormGroup"
                    >
                        <lsq-input
                            class="buyer-filter"
                            placeholder="Search Buyers"
                            formControlName="buyerName"
                            [name]="'buyerName'"
                            [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                            [group]="supplierFiltersFormGroup"
                        >
                        </lsq-input>
                        <lsq-input
                            placeholder="Search Suppliers"
                            formControlName="supplierName"
                            [name]="'supplierName'"
                            [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                            [group]="supplierFiltersFormGroup"
                        >
                        </lsq-input>
                    </div>

                    <div card-body [style.overflowX]="'auto'" *ngIf="dataSource">
                        <lsq-data-table
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                            [columnVisibilities]="{
                                active: showActiveColumn$ | async
                            }"
                        >
                            <lsq-checkbox-column
                                [hideSelectAll]="true"
                                [checkableRowsService]="checkableRowsService"
                                [column]="{
                                    key: 'relationshipKey',
                                    sortable: false
                                }"
                            ></lsq-checkbox-column>
                            <lsq-column
                                [column]="{
                                    header: 'Supplier Name',
                                    key: 'supplierPreferredName',
                                    width: '20%',
                                    truncateTextCss: true
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <div class="text-truncate" [style.maxWidth]="'35rem'">
                                        <a
                                            class="supplier-name"
                                            [routerLink]="
                                                getSupplierInformationLink(row.relationshipKey)
                                            "
                                            queryParamsHandling="preserve"
                                        >
                                            {{ row.supplierPreferredName }}
                                        </a>
                                    </div>
                                </ng-template>
                            </lsq-column>
                            <lsq-column
                                [column]="{
                                    header: 'Supplier ID',
                                    sortable: true,
                                    key: 'platformExternalId',
                                    width: '20%',
                                    truncateAfter: supplierIdTruncateAfter$ | async | notNull,
                                    reverseTruncate: true
                                }"
                            ></lsq-column>
                            <lsq-column
                                [column]="{
                                    header: 'Buyer Relationship',
                                    key: 'buyerPreferredName',
                                    width: '20%',
                                    truncateTextCss: true
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <div class="text-truncate" [style.maxWidth]="'35rem'">
                                        <ng-container *ngIf="canViewBuyers">
                                            <a
                                                class="supplier-name"
                                                [routerLink]="getManageBuyerLink(row.buyerKey)"
                                                [queryParams]="{ visitedFromSuppliers: 'true' }"
                                            >
                                                {{ row.buyerPreferredName }}
                                            </a>
                                        </ng-container>
                                        <ng-container *ngIf="!canViewBuyers">
                                            {{ row.buyerPreferredName }}
                                        </ng-container>
                                    </div>
                                </ng-template>
                            </lsq-column>
                            <lsq-date-column
                                [column]="{
                                    header: 'Date Added',
                                    key: 'dateAdded',
                                    width: '8%'
                                }"
                            >
                            </lsq-date-column>
                            <lsq-column
                                [column]="{
                                    header: 'Enable SCF',
                                    key: 'scfEnabled',
                                    width: '8%',
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <lsq-switch
                                        class="sfc-enable-toggle"
                                        [name]="'scfEnabled'"
                                        [ngModel]="row.scfEnabled"
                                        [disabled]="!canEditSuppliers"
                                        (click)="
                                            canEditSuppliers && openSetScfEnabledModal($event, row)
                                        "
                                    >
                                    </lsq-switch>
                                </ng-template>
                            </lsq-column>
                            <lsq-column
                                [column]="{
                                    header: 'Active',
                                    key: 'active',
                                    width: '8%'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <lsq-switch
                                        class="active-toggle"
                                        [name]="'active'"
                                        [ngModel]="row.active"
                                        [disabled]="!canEditSuppliers"
                                        (click)="
                                            canEditSuppliers && openSetActiveModal($event, row)
                                        "
                                    >
                                    </lsq-switch>
                                </ng-template>
                            </lsq-column>
                            <lsq-column
                                [column]="{
                                    header: 'Eligibility',
                                    key: 'eligible',
                                    width: '8%'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <lsq-switch
                                        class="eligible-toggle"
                                        [name]="'eligible'"
                                        [ngModel]="row.eligible"
                                        [disabled]="!canEditSuppliers"
                                        (click)="
                                            canEditSuppliers && openSetEligibilityModal($event, row)
                                        "
                                    >
                                    </lsq-switch>
                                </ng-template>
                            </lsq-column>
                            <lsq-column
                                [column]="{
                                    header: 'Enrollment',
                                    key: 'enrollmentStatus',
                                    sortable: false,
                                    width: '13%'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <div
                                        class="enrollment-status"
                                        *ngIf="
                                            !row.primaryContactEmail &&
                                            row.enrollmentStatus !== enrollmentStatus.INVITED &&
                                            row.enrollmentStatus !==
                                                enrollmentStatus.NOT_APPLICABLE &&
                                            row.enrollmentStatus !==
                                                enrollmentStatus.USER_CREATED &&
                                            row.enrollmentStatus !== enrollmentStatus.ENROLLED
                                        "
                                    >
                                        <div class="enrollment-status-text">Not Ready</div>
                                        <lsq-button
                                            appearance="transparent"
                                            [routerLink]="
                                                getSupplierImplementationLink(row.relationshipKey)
                                            "
                                        >
                                            Add Contact
                                        </lsq-button>
                                    </div>
                                    <div
                                        class="enrollment-status"
                                        *ngIf="
                                            row.primaryContactEmail &&
                                            row.enrollmentStatus !== enrollmentStatus.INVITED &&
                                            row.enrollmentStatus !==
                                                enrollmentStatus.NOT_APPLICABLE &&
                                            row.enrollmentStatus !==
                                                enrollmentStatus.USER_CREATED &&
                                            row.enrollmentStatus !== enrollmentStatus.ENROLLED
                                        "
                                    >
                                        <div class="enrollment-status-text">Ready For Email</div>
                                        <lsq-button
                                            appearance="transparent"
                                            [routerLink]="
                                                getSupplierImplementationLink(row.relationshipKey)
                                            "
                                        >
                                            Send
                                        </lsq-button>
                                    </div>
                                    <div
                                        class="enrollment-status"
                                        *ngIf="row.enrollmentStatus === enrollmentStatus.INVITED"
                                    >
                                        <div class="enrollment-status-text">
                                            Sent {{ row.enrollmentStatusDate | daysPassed }}
                                        </div>
                                        <lsq-button
                                            appearance="transparent"
                                            [routerLink]="
                                                getSupplierImplementationLink(row.relationshipKey)
                                            "
                                        >
                                            Resend
                                        </lsq-button>
                                    </div>
                                    <div
                                        class="enrollment-status"
                                        *ngIf="
                                            row.enrollmentStatus === enrollmentStatus.USER_CREATED
                                        "
                                    >
                                        <div class="enrollment-status-text">
                                            Enrollment in progress
                                        </div>
                                    </div>
                                    <div
                                        class="enrollment-status"
                                        *ngIf="
                                            row.enrollmentStatus === enrollmentStatus.NOT_APPLICABLE
                                        "
                                    >
                                        <div class="enrollment-status-text">N/A (ELM Platform)</div>
                                    </div>
                                    <div
                                        class="enrollment-status"
                                        *ngIf="row.enrollmentStatus === enrollmentStatus.ENROLLED"
                                    >
                                        <div class="enrollment-status-text">
                                            Enrolled
                                            {{
                                                row.enrollmentStatusDate
                                                    | date : dateFormat.LsqShortYear
                                            }}
                                        </div>
                                    </div>
                                </ng-template>
                            </lsq-column>
                            <lsq-column
                                [column]="{
                                    header: 'Default Payment Method',
                                    key: 'defaultPaymentMethodStatus',
                                    sortable: false,
                                    width: '13%'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <div class="payment-method-column">
                                        <status-pill
                                            [status]="row.defaultPaymentMethodStatus"
                                            [displayValueMap]="paymentMethodStatusDisplay"
                                        ></status-pill>
                                        <a
                                            [style.height]="'2rem'"
                                            [routerLink]="
                                                getSupplierPaymentMethodsLink(row.relationshipKey)
                                            "
                                        >
                                            <lsq-icon
                                                [type]="icons.FaArrowRight"
                                                [height]="20"
                                            ></lsq-icon>
                                        </a>
                                    </div>
                                </ng-template>
                            </lsq-column>
                        </lsq-data-table>
                    </div>
                </card>
            </div>
        </div>
    `
})
@LifecycleAware()
export class AdminSuppliersComponent implements OnDestroy {
    public paymentMethodStatusDisplay = PaymentMethodStatusDisplay;
    public visitedFromBuyerKey: string = null;
    public visitedFromBuyerName: string = null;

    private INACTIVE_TITLE = 'Set Supplier Inactive';
    private INACTIVE_BUTTON = 'Set Supplier Inactive';

    private ACTIVE_TITLE = 'Set Supplier Active';
    private ACTIVE_BUTTON = 'Set Supplier Active';

    private INELIGIBLE_TITLE = 'Disable Early Payment Eligibility';
    private INELIGIBLE_BUTTON = 'Disable Eligibility';

    private ELIGIBLE_TITLE = 'Enable Early Payment Eligibility';
    private ELIGIBLE_BUTTON = 'Enable Eligibility';

    private SCF_DISABLED_TITLE = 'Disable SCF';
    private SCF_DISABLED_BUTTON = 'Disable SCF';

    private SCF_ENABLED_TITLE = 'Enable SCF';
    private SCF_ENABLED_BUTTON = 'Enable SCF';

    //Constants
    public readonly classNames = ClassNames;
    public readonly dateFormat = DateFormat;
    public readonly styling = Styling;
    public readonly statusColor = StatusColor;
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly enrollmentStatus = EnrollmentStatus;
    public readonly debounceTime = Interaction.SearchDebounceTimeMs;
    public canEditSuppliers = false;
    public canViewBuyers = false;

    public adminManageSupplierImplementationLinkUrl = getRouterLinkUrlById(
        atlas,
        ADMIN_MANAGE_SUPPLIER_IMPLEMENTATION
    );
    public adminManageSupplierInformationLinkUrl = getRouterLinkUrlById(
        atlas,
        ADMIN_MANAGE_SUPPLIER_INFORMATION
    );
    public adminManageSupplierPaymentMethodsLinkUrl = getRouterLinkUrlById(
        atlas,
        ADMIN_MANAGE_SUPPLIER_PAYMENT_METHODS
    );
    public adminManageBuyerLinkUrl = getRouterLinkUrlById(atlas, ADMIN_MANAGE_BUYER_COMPANY);

    public showActiveColumn$ = this._featureFlagService.isOn(
        FeatureFlags.AdminSupplierActiveToggle
    );

    public readonly supplierIdTruncateAfter$ = this._windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1525) {
                return 35;
            }
            if (dimensions.width > 1100) {
                return 25;
            }
            return 15;
        })
    );

    public dataSource: LsqApiServiceListSource<
        ListResponseSupplierDto,
        ListSuppliersRequest,
        SupplierDto
    >;
    public checkableRowsService: CheckableRowsService;

    public bulkActionType$ = this._adminSuppliersBulkActionsService.bulkActionType$;

    public supplierFiltersFormGroup = this._adminSuppliersService.supplierFiltersFormGroup;

    constructor(
        private _adminSuppliersService: AdminSuppliersService,
        private _adminSuppliersBulkActionsService: AdminSuppliersBulkActionsService,
        private _modalService: ModalService,
        private _featureFlagService: FeatureFlagService,
        private _claimService: ClaimService,
        private _router: Router,
        private _windowReferenceService: WindowReferenceService,
        private _activatedRoute: ActivatedRoute
    ) {
        this.canEditSuppliers = this._claimService.isOnSync(DashboardClaim.AdminEditSupplier);
        this.canViewBuyers = this._claimService.isOnSync(DashboardClaim.AdminViewBuyer);

        this._adminSuppliersService.initializeSuppliersListSource();
        this.dataSource = this._adminSuppliersService.dataSource;

        this._adminSuppliersBulkActionsService.bulkActionComplete$
            .pipe(forLifeOf(this))
            .subscribe(() => {
                this.checkableRowsService.uncheckAll();
                this.dataSource.dispatchRequest();
            });
    }

    public async ngOnInit(): Promise<void> {
        const supplierFilterValueChanges = this.supplierFiltersFormGroup.valueChanges.pipe(
            forLifeOf(this),
            distinctUntilChanged(),
            debounceTime(this.debounceTime),
            shareReplay(1)
        );
        supplierFilterValueChanges.subscribe((supplierFilters) => {
            this._adminSuppliersService.dispatchFilterChange(supplierFilters);

            this.dataSource.dispatchRequest({
                pagination: { skip: 0 },
                filter: {
                    buyerName: supplierFilters.buyerName,
                    supplierName: supplierFilters.supplierName
                }
            });
        });

        this._activatedRoute.queryParams.pipe(forLifeOf(this)).subscribe((queryParams) => {
            if (!!queryParams.buyerName) {
                this.supplierFiltersFormGroup.get('buyerName').setValue(queryParams.buyerName);
                this.visitedFromBuyerName = queryParams.buyerName;
            } else {
                this.visitedFromBuyerName = null;
            }

            if (!!queryParams.buyerKey) {
                this.visitedFromBuyerKey = queryParams.buyerKey;
            } else {
                this.visitedFromBuyerKey = null;
            }
        });

        this._adminSuppliersService.dispatchFilterChange({
            ...this.supplierFiltersFormGroup.value
        });
        await this._adminSuppliersService.initCheckableService();
        this.checkableRowsService = this._adminSuppliersService.checkableRowsService;
    }

    public ngOnDestroy(): void {
        this._adminSuppliersBulkActionsService.cancelBulkAction();
    }

    public getSupplierImplementationLink(relationshipKey: Guid): string {
        return getDynamicRouteLink(this.adminManageSupplierImplementationLinkUrl, {
            relationshipKey
        });
    }

    public getSupplierInformationLink(relationshipKey: Guid): string {
        return getDynamicRouteLink(this.adminManageSupplierInformationLinkUrl, {
            relationshipKey
        });
    }

    public getManageBuyerLink(buyerKey: Guid): string {
        return getDynamicRouteLink(this.adminManageBuyerLinkUrl, {
            buyerKey
        });
    }

    public getSupplierPaymentMethodsLink(relationshipKey: Guid): string {
        return getDynamicRouteLink(this.adminManageSupplierPaymentMethodsLinkUrl, {
            relationshipKey
        });
    }

    public async openSetActiveModal(event: Event, row: SupplierDto): Promise<void> {
        event.preventDefault();

        const isSubmittingSubject = new BehaviorSubject<boolean>(false);

        this._modalService.showModal(
            new Modal({
                id: 'set-supplier-active-confirmation-modal',
                title: !row.active ? this.ACTIVE_TITLE : this.INACTIVE_TITLE,
                bodyComponent: ToggleSupplierActivationModalComponent,
                showFooter: true,
                showOkButton: true,
                showCancelButton: true,
                showCloseButton: true,
                okButtonSize: ClassNames.Block,
                okButtonText: !row.active ? this.ACTIVE_BUTTON : this.INACTIVE_BUTTON,
                isSubmitting$: isSubmittingSubject,
                data: {
                    supplierRelationship: row
                },
                onOkClick: async () => {
                    isSubmittingSubject.next(true);
                    await this._adminSuppliersService.setSuppliersActiveState(
                        [row.supplierKey],
                        !row.active
                    );
                    isSubmittingSubject.next(false);
                    this._modalService.closeModal();
                    this.dataSource.dispatchRequest();
                },
                onCancelClick: () => this._modalService.closeModal()
            })
        );
    }

    public openSetEligibilityModal(event: Event, row: SupplierDto): void {
        event.preventDefault();

        const isSubmittingSubject = new BehaviorSubject<boolean>(false);
        const okButtonIsDisabled$ = new BehaviorSubject<boolean>(true);

        this._modalService.showModal(
            new Modal({
                id: 'set-supplier-eligibility-confirmation-modal',
                title: !row.eligible ? this.ELIGIBLE_TITLE : this.INELIGIBLE_TITLE,
                bodyComponent: ToggleSupplierEligibilityModalComponent,
                showFooter: true,
                showOkButton: true,
                showCancelButton: true,
                showCloseButton: true,
                okButtonSize: ClassNames.Block,
                okButtonText: !row.eligible ? this.ELIGIBLE_BUTTON : this.INELIGIBLE_BUTTON,
                isSubmitting$: isSubmittingSubject,
                okButtonIsDisabled$,
                data: {
                    supplierRelationship: row,
                    okButtonIsDisabled$
                },
                onOkClick: async () => {
                    isSubmittingSubject.next(true);

                    await this._adminSuppliersService.setSuppliersEligibility(
                        { supplierKey: row.supplierKey, relationshipKey: row.relationshipKey },
                        !row.eligible
                    );

                    isSubmittingSubject.next(false);
                    this._modalService.closeModal();
                    this.dataSource.dispatchRequest();
                },
                onCancelClick: () => {
                    this._modalService.closeModal();
                }
            })
        );
    }

    public async openSetScfEnabledModal(event: Event, row: SupplierDto): Promise<void> {
        event.preventDefault();

        const isSubmittingSubject = new BehaviorSubject<boolean>(false);
        const okButtonIsDisabled$ = new BehaviorSubject<boolean>(true);

        this._modalService.showModal(
            new Modal({
                id: 'set-scf-enable-confirmation-modal',
                title: !row.scfEnabled ? this.SCF_ENABLED_TITLE : this.SCF_DISABLED_TITLE,
                bodyComponent: ToggleScfEnabledModalComponent,
                showFooter: true,
                showOkButton: true,
                showCancelButton: true,
                showCloseButton: true,
                okButtonSize: ClassNames.Block,
                okButtonText: !row.scfEnabled ? this.SCF_ENABLED_BUTTON : this.SCF_DISABLED_BUTTON,
                isSubmitting$: isSubmittingSubject,
                okButtonIsDisabled$,
                data: {
                    supplierRelationship: row,
                    okButtonIsDisabled$
                },
                onOkClick: async () => {
                    isSubmittingSubject.next(true);

                    await this._adminSuppliersService.setSuppliersScfEnabledState(
                        row.relationshipKey,
                        row.scfEnabled === 0 ? 1 : 0
                    );

                    isSubmittingSubject.next(false);
                    this._modalService.closeModal();
                    this.dataSource.dispatchRequest();
                },
                onCancelClick: () => {
                    this._modalService.closeModal();
                }
            })
        );
    }

    public navigateToCreateSuppliersPage(): void {
        this._router.navigateByUrl(getRouterLinkUrlById(atlas, ADMIN_CREATE_SUPPLIER));
    }

    public navigateBackToBuyer(): void {
        if (this._claimService.isOnSync(DashboardClaim.AdminViewBuyer)) {
            if (this.visitedFromBuyerKey) {
                const urlTree = this._router.createUrlTree(
                    getDynamicRouterLink(
                        getRouterLinkById(atlas, ADMIN_MANAGE_BUYER_COMPANY, {
                            noStandaloneLeadingSlash: true
                        }),
                        { buyerKey: this.visitedFromBuyerKey }
                    )
                );

                this._router.navigateByUrl(urlTree);
            } else {
                this._router.navigateByUrl(getRouterLinkUrlById(atlas, ADMIN_BUYERS));
            }
        }
    }

    public bulkSetEligibility(eligible: boolean): void {
        this._adminSuppliersBulkActionsService.initializeBulkEligibilityAction(
            this.checkableRowsService.checkedRows as string[],
            eligible
        );
    }

    public bulkEnableScf(scf: boolean): void {
        this._adminSuppliersBulkActionsService.initializeBulkScfEnabledAction(
            this.checkableRowsService.checkedRows as string[],
            scf
        );
    }

    public bulkSendEnrollmentEmail(): void {
        this._adminSuppliersBulkActionsService.initializeBulkEnrollmentInviteAction(
            this.checkableRowsService.checkedRows as string[]
        );
    }

    public bulkSetFeeStructure(): void {
        this._adminSuppliersBulkActionsService.initializeBulkFeeStructureAction(
            this.checkableRowsService.checkedRows as string[]
        );
    }
}
